import styled from "@emotion/styled";
import { FC } from "react";

import { DesignToken, RadiusToken, SpacingToken, Tag, Text } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { CustomFieldTypes } from "constants/custom-fields";
import { ResponsiveBreakpoint, ResponsiveBreakpointType } from "constants/responsive-breakpoint";
import { withVar } from "themes/helpers";

type ICustomFieldPreviewStylesProps = {
  customFieldType?: CustomFieldTypes;
};

export const CustomFieldPreview: FC<ICustomFieldPreviewStylesProps> = ({ customFieldType }) => (
  <CustomFieldPreviewContainer>
    <Block margin={withVar(SpacingToken.Spacing5)} maxWidth="348px">
      <Tag>
        <Text bold>Preview</Text>
      </Tag>
      {customFieldType ? (
        <>
          <PreviewCardWrapper>
            <Flex column>
              <Header bold>Ticket info</Header>
              <Flex flexWrap>
                <Flex>
                  <Skeleton width="36px" />
                  <Skeleton width="123px" />
                </Flex>
                <Flex>
                  <Skeleton width="25px" />
                  <Skeleton width="75px" />
                </Flex>
                <Flex>
                  <Skeleton width="36px" />
                  <Skeleton width="109px" />
                </Flex>
                {PREVIEW_COMPONENT[customFieldType]}
              </Flex>
            </Flex>
          </PreviewCardWrapper>
          <PreviewCardWrapper>
            <Flex column>
              <Header bold>Responsibility</Header>
              <Flex flexWrap>
                <AvatarSkeleton width="36px" />
                <Flex column>
                  <Skeleton width="123px" />
                  <Skeleton width="53px" />
                </Flex>
              </Flex>
            </Flex>
          </PreviewCardWrapper>{" "}
        </>
      ) : (
        <EmptyHeader>Choose the custom field type to see its preview.</EmptyHeader>
      )}
    </Block>
  </CustomFieldPreviewContainer>
);

const CustomFieldPreviewContainer = styled.div`
  background-color: ${withVar(DesignToken.SurfaceSecondaryDefault)};
  border-radius: ${withVar(RadiusToken.Radius3)};
  width: 100%;
  max-width: 358px;
  @media (max-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile]}px) {
    display: none;
  }
`;

const Skeleton = styled.span<{ width: string }>`
  background-color: ${withVar(DesignToken.SurfaceTertiaryDefault)};
  border-radius: ${withVar(RadiusToken.Radius3)};
  margin: var(${SpacingToken.Spacing2}) var(${SpacingToken.Spacing2}) 0 0;
  height: 10px;
  width: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.width
  };
`;

const ActiveSkeleton = styled(Skeleton)`
  background-color: ${withVar(DesignToken.ActionPrimaryDefault)};
`;

const AvatarSkeleton = styled(Skeleton)`
  border-radius: 50%;
  height: 36px;
  width: 36px;
`;

const PreviewCardWrapper = styled.div`
  background-color: ${withVar(DesignToken.SurfacePrimaryDefault)};
  border-radius: ${withVar(RadiusToken.Radius3)};
  padding: 20px;
  margin: var(${SpacingToken.Spacing8});
`;

const Header = styled(Text)`
  margin-bottom: var(${SpacingToken.Spacing2});
`;

const EmptyHeader = styled(Text)`
  margin-top: var(${SpacingToken.Spacing4});
`;

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const SingleLinePreview = () => (
  <Flex>
    <ActiveSkeleton width="124px" />
  </Flex>
);

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const MultilinePreview = () => (
  <Flex column>
    <ActiveSkeleton width="124px" />
    <ActiveSkeleton width="64px" />
    <ActiveSkeleton width="108px" />
  </Flex>
);

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const DatePreview = () => (
  <Flex>
    <ActiveSkeleton width="18px" />
    <ActiveSkeleton width="18px" />
    <ActiveSkeleton width="48px" />
  </Flex>
);

const PREVIEW_COMPONENT = {
  [CustomFieldTypes.SingleLine]: SingleLinePreview(),
  [CustomFieldTypes.MultiLine]: MultilinePreview(),
  [CustomFieldTypes.Date]: DatePreview(),
  [CustomFieldTypes.Url]: SingleLinePreview(),
};
